import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// سياق كل صفحة: عنوان مبسّط (بلا بيانات حساسة) + 3 أسئلة سريعة مقترحة
const PAGE_CONTEXTS = [
  { test: (p) => p.startsWith('/coach'), label: 'بوابة المدرب', questions: ['كيف أضيف تمرين جديد لمكتبتي؟', 'كيف أنشئ خطة تدريبية لفريقي؟', 'كيف أرسل تقرير أداء لمنشأتي؟'] },
  { test: (p) => p.startsWith('/players'), label: 'صفحة اللاعبين', questions: ['كيف أضيف لاعب جديد؟', 'كيف أسجل اشتراك لاعب؟', 'كيف أطبع قائمة لاعبي فئة معينة؟'] },
  { test: (p) => p.startsWith('/teams'), label: 'الفرق والفئات', questions: ['كيف أنشئ فئة جديدة؟', 'كيف أسند لاعبين لفريق؟', 'كيف أعرض لاعبي فئة معينة؟'] },
  { test: (p) => p.startsWith('/attendance'), label: 'الحضور والانصراف', questions: ['كيف أسجل حضور اللاعبين بالباركود؟', 'كيف أستخرج تقرير حضور شهري؟', 'ما الفرق بين الغياب العادي والمعتمد؟'] },
  { test: (p) => ['/accounts', '/expenses', '/revenues', '/payroll', '/budget', '/financial-reports', '/invoices', '/quotes'].some((x) => p.startsWith(x)), label: 'الشؤون المالية', questions: ['كيف أسجل مصروف جديد؟', 'كيف أنشئ فاتورة لعميل؟', 'كيف أصدر تقرير مالي شهري؟'] },
  { test: (p) => ['/staff-admin', '/coaching-staff', '/medical-staff', '/security-staff', '/general-services'].some((x) => p.startsWith(x)), label: 'إدارة الموظفين', questions: ['كيف أضيف موظف جديد؟', 'كيف أنشئ عقد لموظف؟', 'كيف أحتسب راتب موظف؟'] },
  { test: (p) => p.startsWith('/communication'), label: 'التواصل والرسائل', questions: ['كيف أرسل رسالة واتساب جاهزة؟', 'كيف أعدّل قوالب الرسائل؟', 'كيف أرسل رسالة لولي أمر معين؟'] },
  { test: (p) => p.startsWith('/settings'), label: 'إعدادات المنشأة', questions: ['كيف أغيّر شعار منشأتي؟', 'كيف أضيف مستخدم جديد؟', 'كيف أفعّل واتساب للتواصل؟'] },
  { test: (p) => p.startsWith('/integrations'), label: 'التطبيقات والتكاملات', questions: ['ما هي رسائل واتساب الجاهزة؟', 'كيف أسجل اهتمامي بميزة كارتات؟', 'هل هناك تكاملات أخرى قادمة؟'] },
  { test: (p) => p.startsWith('/support'), label: 'صفحة الدعم', questions: ['كيف أرفع تذكرة دعم جديدة؟', 'كم يستغرق الرد على التذاكر؟', 'أين أجد إجابات الأسئلة الشائعة؟'] },
  { test: (p) => p === '/' || p.startsWith('/dashboard'), label: 'الرئيسية', questions: ['كيف أبدأ استخدام تسيار؟', 'ما هي أهم الميزات المتاحة؟', 'كيف أدعو موظفين لمنشأتي؟'] },
];
const DEFAULT_CONTEXT = { label: 'منصة تسيار', questions: ['كيف أضيف لاعب جديد؟', 'كيف أرسل رسالة واتساب جاهزة؟', 'كيف أسجل مصروف مالي؟'] };

function getPageContext(pathname = '') {
  return PAGE_CONTEXTS.find((c) => c.test(pathname)) || DEFAULT_CONTEXT;
}

const seenSet = (key) => new Set(JSON.parse(localStorage.getItem(key) || '[]'));
const persistSet = (key, set) => localStorage.setItem(key, JSON.stringify([...set]));

// عرض مبسّط لنص المساعد: يحوّل **غامق** لنص عريض ويحافظ على فواصل الأسطر (بلا مكتبة markdown خارجية)
function renderMessageContent(content) {
  if (!content) return null;
  return content.split('\n').map((line, li) => (
    <React.Fragment key={li}>
      {li > 0 && <br />}
      {line.split(/(\*\*[^*]+\*\*)/g).map((part, pi) =>
        part.startsWith('**') && part.endsWith('**')
          ? <strong key={pi}>{part.slice(2, -2)}</strong>
          : <span key={pi}>{part}</span>
      )}
    </React.Fragment>
  ));
}

const ChatWidget = ({ user, clubSettings, hideFloatingButtonMobile, pathname = '' }) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [mode, setMode] = useState('ai'); // ai | updates
  const [hasUnreadUpdates, setHasUnreadUpdates] = useState(false);
  const [importantCard, setImportantCard] = useState(null);
  const [dismissedIds, setDismissedIds] = useState(() => seenSet('tasyar_dismissed_updates'));

  const pageContext = useMemo(() => getPageContext(pathname), [pathname]);

  // ----- AI assistant state -----
  const [sessionId] = useState(() => {
    const existing = sessionStorage.getItem('tasyar_ai_chat_session');
    if (existing) return existing;
    const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    sessionStorage.setItem('tasyar_ai_chat_session', id);
    return id;
  });
  const [aiMessages, setAiMessages] = useState([]);
  const [aiInput, setAiInput] = useState('');
  const [aiStreaming, setAiStreaming] = useState(false);
  const aiEndRef = useRef(null);

  // ----- updates feed state -----
  const [updates, setUpdates] = useState([]);
  const [updatesLoading, setUpdatesLoading] = useState(false);

  const fetchUpdates = async () => {
    setUpdatesLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE_URL}/api/updates/mine`, { headers: { Authorization: `Bearer ${token}` } });
      const items = (Array.isArray(res.data) ? res.data : []).map((it) => ({
        uid: it.id, id: it.id, title: it.title, content: it.description, badge: it.badge,
        image_url: it.image_url, banner_image_url: it.banner_image_url, link: it.link,
        priority: it.priority || 'normal', created_at: it.publish_date, is_read: !!it.is_read,
      }));
      setUpdates(items);
      const unread = items.filter((u) => !u.is_read);
      setHasUnreadUpdates(unread.length > 0);

      const important = unread.find((u) => (u.priority === 'high' || u.priority === 'urgent') && !dismissedIds.has(u.uid));
      setImportantCard(important || null);
    } catch (e) {
      // صامت — لا داعي لإزعاج المستخدم بأخطاء جلب التحديثات
    } finally {
      setUpdatesLoading(false);
    }
  };

  useEffect(() => {
    if (!user) return;
    fetchUpdates();
    const interval = setInterval(fetchUpdates, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, [user]);

  useEffect(() => {
    const handler = (e) => { setIsOpen(true); setMode(e?.detail?.mode === 'ticket' ? 'updates' : (e?.detail?.mode || 'ai')); };
    window.addEventListener('tasyar:open-chat-widget', handler);
    return () => window.removeEventListener('tasyar:open-chat-widget', handler);
  }, []);

  useEffect(() => {
    const onHide = () => setHidden(true);
    const onShow = () => setHidden(false);
    window.addEventListener('tasyar:hide-chat-widget', onHide);
    window.addEventListener('tasyar:show-chat-widget', onShow);
    return () => {
      window.removeEventListener('tasyar:hide-chat-widget', onHide);
      window.removeEventListener('tasyar:show-chat-widget', onShow);
    };
  }, []);

  useEffect(() => {
    if (isOpen && mode === 'ai' && aiMessages.length === 0) {
      axios.get(`${API_BASE_URL}/api/support/ai-chat/history/${sessionId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      }).then((res) => {
        if (Array.isArray(res.data) && res.data.length) setAiMessages(res.data);
        else setAiMessages([{ role: 'assistant', content: 'أهلاً بك 👋 أنا مساعد تسيار، اسألني عن أي شيء في المنصة وسأساعدك فوراً.' }]);
      }).catch(() => setAiMessages([{ role: 'assistant', content: 'أهلاً بك 👋 أنا مساعد تسيار، اسألني عن أي شيء في المنصة وسأساعدك فوراً.' }]));
    }
    // eslint-disable-next-line
  }, [isOpen, mode]);

  useEffect(() => { aiEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [aiMessages]);

  const sendAiMessage = async (e, forcedText) => {
    if (e?.preventDefault) e.preventDefault();
    const text = (forcedText ?? aiInput).trim();
    if (!text || aiStreaming) return;
    setAiInput('');
    setAiMessages((prev) => [...prev, { role: 'user', content: text }, { role: 'assistant', content: '' }]);
    setAiStreaming(true);
    try {
      const token = localStorage.getItem('token');
      const resp = await fetch(`${API_BASE_URL}/api/support/ai-chat/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ session_id: sessionId, message: text, page_context: pageContext.label }),
      });
      if (!resp.ok || !resp.body) throw new Error('stream failed');
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const raw = line.slice(6);
          if (raw === '[DONE]') continue;
          let chunk;
          try { chunk = JSON.parse(raw); } catch { chunk = raw; }
          setAiMessages((prev) => {
            const copy = [...prev];
            copy[copy.length - 1] = { role: 'assistant', content: copy[copy.length - 1].content + chunk };
            return copy;
          });
        }
      }
    } catch (err) {
      setAiMessages((prev) => {
        const copy = [...prev];
        copy[copy.length - 1] = { role: 'assistant', content: 'تعذّر الوصول للمساعد الذكي حالياً. يمكنك التوجّه لصفحة الدعم لرفع تذكرة.' };
        return copy;
      });
    } finally {
      setAiStreaming(false);
    }
  };

  const markUpdateRead = async (item) => {
    setUpdates((prev) => prev.map((u) => (u.uid === item.uid ? { ...u, is_read: true } : u)));
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE_URL}/api/updates/mark-read`, { update_id: item.id }, { headers: { Authorization: `Bearer ${token}` } });
    } catch (e) { /* ignore */ }
    setHasUnreadUpdates(updates.some((u) => u.uid !== item.uid && !u.is_read));
  };

  const dismissImportantCard = () => {
    if (!importantCard) return;
    const next = new Set(dismissedIds); next.add(importantCard.uid);
    setDismissedIds(next);
    persistSet('tasyar_dismissed_updates', next);
    setImportantCard(null);
  };

  const openUpdateFromCard = () => {
    if (importantCard) markUpdateRead(importantCard);
    dismissImportantCard();
    setIsOpen(true);
    setMode('updates');
  };

  const goToSupport = () => {
    if (!user) return;
    setIsOpen(false);
    navigate('/support');
  };

  const formatDate = (dateString) => dateString ? new Date(dateString).toLocaleDateString('ar-SA', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';

  if (hidden) return null;

  const importantCardOffset = user && !user.email_verified ? 'bottom-[290px] lg:bottom-[224px]' : 'bottom-[168px] lg:bottom-24';

  return (
    <>
      {importantCard && !isOpen && (
        <div
          dir="rtl"
          data-testid="chat-widget-important-card"
          className={`fixed ${importantCardOffset} left-6 z-40 flex max-w-[280px] items-start gap-2.5 rounded-2xl border border-orange-200 bg-white p-3.5 shadow-xl`}
        >
          <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-orange-100 text-orange-600">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="h-4 w-4">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m0 3.75h.008M10.34 3.94l-8.1 14.03A1.5 1.5 0 003.55 20.5h16.9a1.5 1.5 0 001.31-2.53l-8.1-14.03a1.5 1.5 0 00-2.62 0z" />
            </svg>
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-xs font-bold leading-snug text-slate-800">{importantCard.title}</p>
            <p className="mt-0.5 text-[11px] leading-snug text-slate-500 line-clamp-2">{importantCard.content}</p>
            <div className="mt-2 flex items-center gap-3">
              <button type="button" onClick={openUpdateFromCard} data-testid="chat-widget-important-card-open" className="rounded-lg bg-orange-500 px-3 py-1.5 text-xs font-bold text-white hover:bg-orange-600 transition-colors">
                عرض التفاصيل
              </button>
              <button type="button" onClick={dismissImportantCard} data-testid="chat-widget-important-card-dismiss" className="text-xs font-medium text-slate-400 hover:text-slate-600">
                إغلاق
              </button>
            </div>
          </div>
        </div>
      )}

      <div className={`fixed ${hideFloatingButtonMobile ? 'bottom-40' : 'bottom-24'} left-6 z-50 lg:bottom-6`}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          data-testid="chat-widget-toggle-btn"
          className={`relative w-14 h-14 rounded-full shadow-xl transition-transform duration-200 hover:scale-105 active:scale-95 overflow-hidden
            ${isOpen ? 'bg-gradient-to-br from-slate-600 to-slate-700' : 'bg-[#0A192F]'} border-2 border-white/30`}
          title={isOpen ? 'إغلاق مساعد تسيار' : 'مساعد تسيار'}
        >
          <div className="absolute inset-0 flex items-center justify-center">
            {isOpen ? (
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            ) : (
              <img src="/club-logo-192.png?v=20260915b" alt="مساعد تسيار" className="w-full h-full object-cover" />
            )}
          </div>
          {hasUnreadUpdates && !isOpen && (
            <span className="absolute top-1 right-1 h-3 w-3 rounded-full bg-red-500 border-2 border-white" data-testid="chat-widget-unread-dot" />
          )}
        </button>
      </div>

      {isOpen && (
        <div className="fixed bottom-[168px] left-6 w-[calc(100vw-3rem)] max-w-[380px] h-[540px] bg-white rounded-3xl shadow-2xl border border-slate-200 z-40 flex flex-col overflow-hidden animate-slideInUp lg:bottom-24" data-testid="chat-widget-window">
          <div className="bg-[#0A192F] p-4 text-white">
            <div className="flex items-center gap-2.5">
              <img src="/club-logo-192.png?v=20260915b" alt="" className="h-9 w-9 rounded-xl shrink-0" />
              <div className="min-w-0">
                <h3 className="font-bold text-base">مساعد تسيار</h3>
                <p className="text-white/60 text-[11px] truncate">هنا لمساعدتك في {pageContext.label}</p>
              </div>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-1.5 rounded-xl bg-white/10 p-1">
              <button onClick={() => setMode('ai')} data-testid="chat-widget-tab-ai" className={`rounded-lg py-1.5 text-xs font-bold transition-colors ${mode === 'ai' ? 'bg-white text-[#0A192F]' : 'text-white/80 hover:text-white'}`}>المساعد الذكي</button>
              <button onClick={() => setMode('updates')} data-testid="chat-widget-tab-updates" className={`relative rounded-lg py-1.5 text-xs font-bold transition-colors ${mode === 'updates' ? 'bg-white text-[#0A192F]' : 'text-white/80 hover:text-white'}`}>
                التحديثات
                {hasUnreadUpdates && <span className="absolute top-1 left-2 h-1.5 w-1.5 rounded-full bg-red-500" />}
              </button>
            </div>
          </div>

          {mode === 'ai' ? (
            <>
              <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50" data-testid="chat-widget-ai-messages">
                {aiMessages.map((m, i) => (
                  <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-[15px] leading-relaxed shadow-sm ${m.role === 'user' ? 'bg-teal-600 text-white' : 'bg-white border border-slate-200 text-slate-800'}`}>
                      {m.content ? renderMessageContent(m.content) : (aiStreaming && i === aiMessages.length - 1 ? <span className="inline-flex gap-1"><span className="h-1.5 w-1.5 animate-bounce rounded-full bg-slate-400" /><span className="h-1.5 w-1.5 animate-bounce rounded-full bg-slate-400 [animation-delay:0.15s]" /><span className="h-1.5 w-1.5 animate-bounce rounded-full bg-slate-400 [animation-delay:0.3s]" /></span> : null)}
                    </div>
                  </div>
                ))}
                <div ref={aiEndRef} />
              </div>
              <div className="border-t border-slate-100 bg-white px-3 py-2 text-center">
                <button onClick={goToSupport} data-testid="chat-widget-escalate-btn" className="text-xs font-semibold text-teal-700 hover:underline">لم يُحل استفسارك؟ افتح تذكرة دعم من صفحة الدعم ←</button>
              </div>
              <div className="flex gap-1.5 overflow-x-auto border-t border-slate-100 bg-white px-3 pt-2.5 pb-1 no-scrollbar">
                {pageContext.questions.map((q, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => sendAiMessage(null, q)}
                    disabled={aiStreaming}
                    data-testid={`chat-widget-quick-question-${i}`}
                    className="shrink-0 whitespace-nowrap rounded-full border border-teal-200 bg-teal-50 px-3 py-1.5 text-[11px] font-semibold text-teal-700 hover:bg-teal-100 transition-colors disabled:opacity-50"
                  >
                    {q}
                  </button>
                ))}
              </div>
              <form onSubmit={sendAiMessage} className="p-3 bg-white border-t border-slate-100">
                <div className="flex gap-2">
                  <input
                    value={aiInput}
                    onChange={(e) => setAiInput(e.target.value)}
                    placeholder="اكتب سؤالك هنا..."
                    disabled={aiStreaming}
                    data-testid="chat-widget-ai-input"
                    className="flex-1 px-3 py-2 border border-slate-300 rounded-xl text-[15px] focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                  <button type="submit" disabled={aiStreaming || !aiInput.trim()} data-testid="chat-widget-ai-send-btn" className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-xl transition-colors disabled:opacity-50 flex items-center justify-center min-w-[48px]">
                    {aiStreaming ? <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div> : <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" /></svg>}
                  </button>
                </div>
              </form>
            </>
          ) : (
            <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50" data-testid="chat-widget-updates-list">
              {updatesLoading && updates.length === 0 ? (
                <p className="text-center text-sm text-slate-400 py-8">جارٍ التحميل...</p>
              ) : updates.length === 0 ? (
                <p className="text-center text-sm text-slate-400 py-8">لا توجد تحديثات جديدة حالياً</p>
              ) : (
                updates.map((u) => (
                  <div
                    key={u.uid}
                    onClick={() => !u.is_read && markUpdateRead(u)}
                    data-testid={`chat-widget-update-${u.uid}`}
                    className={`overflow-hidden rounded-2xl border shadow-sm cursor-pointer transition-colors ${u.is_read ? 'bg-white border-slate-100 opacity-70' : 'bg-white border-teal-200'}`}
                  >
                    {u.banner_image_url && (
                      <img src={`${API_BASE_URL}${u.banner_image_url}`} alt="" className="h-20 w-full object-cover" />
                    )}
                    <div className="p-3.5">
                      <div className="flex items-start gap-2.5">
                        {u.image_url ? (
                          <img src={`${API_BASE_URL}${u.image_url}`} alt="" className="h-9 w-9 shrink-0 rounded-lg object-cover" />
                        ) : (
                          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-teal-50 text-teal-600 text-sm font-bold">✦</span>
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center justify-between gap-2">
                            <h4 className="text-sm font-bold text-slate-800">{u.title}</h4>
                            {!u.is_read && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-red-500" />}
                          </div>
                          {u.badge && <span className="mt-1 inline-block rounded-full bg-orange-50 px-2 py-0.5 text-[10px] font-bold text-orange-600">{u.badge}</span>}
                        </div>
                      </div>
                      <p className="mt-2 text-[13px] leading-relaxed text-slate-600">{u.content}</p>
                      <div className="mt-1.5 flex items-center justify-between">
                        <p className="text-[11px] text-slate-400">{formatDate(u.created_at)}</p>
                        {u.link && (
                          <a
                            href={u.link}
                            target={u.link.startsWith('/') ? undefined : '_blank'}
                            rel={u.link.startsWith('/') ? undefined : 'noopener noreferrer'}
                            onClick={(e) => e.stopPropagation()}
                            className="text-[11px] font-bold text-teal-600 hover:underline"
                            data-testid={`chat-widget-update-link-${u.uid}`}
                          >
                            اكتشف أكثر ←
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
          {mode === 'updates' && (
            <div className="border-t border-slate-100 bg-white px-3 py-2 text-center">
              <a href="/updates" target="_blank" rel="noreferrer" data-testid="chat-widget-view-all-updates" className="text-xs font-semibold text-teal-700 hover:underline">عرض الأرشيف الكامل للتحديثات ←</a>
            </div>
          )}
        </div>
      )}

      <style>{`
        @keyframes slideInUp { from { opacity: 0; transform: translateY(60px); } to { opacity: 1; transform: translateY(0); } }
        .animate-slideInUp { animation: slideInUp 0.25s ease-out; }
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { scrollbar-width: none; }
      `}</style>
    </>
  );
};

export default ChatWidget;
